import DOMPurify from "dompurify";
import RichTextEditor from "./RichTextEditor";
import React, { useCallback, useState, Fragment } from "react";
import makeStyles from "@mui/styles/makeStyles";
import { FormHelperText, LinearProgress, Tooltip } from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleInfo } from "@fortawesome/pro-regular-svg-icons";

const ALLOWED_TAGS = [
  "p",
  "br",
  "span",
  "strong",
  "em",
  "u",
  "b",
  "i",
  "a",
  "ul",
  "ol",
  "li",
  "h1",
  "h2",
  "h3",
  "h4",
  "h5",
  "h6",
  "blockquote",
  "pre",
  "code",
];
const ALLOWED_ATTR = ["href", "target", "rel"];

const sanitizeHtml = html =>
  DOMPurify.sanitize(html, { ALLOWED_TAGS, ALLOWED_ATTR });

const useStyles = makeStyles(theme => ({
  rte: {
    borderWidth: 0,
    fontFamily: "inherit",
    resize: "both",
    overflow: "auto",

    "& a": {
      color: theme.palette.primary.main,
    },
  },

  counterArea: {
    flexGrow: 1,
    textAlign: "right",
    marginTop: 5,
  },

  counterRow: {
    display: "flex",
    justifyContent: "flex-end",
    alignItems: "center",
    gap: 4,
  },

  counterText: {
    fontSize: "1.5rem",
  },

  counterNormal: {
    color: "rgba(0,0,0,0.54)",
  },

  counterWarning: {
    color: theme.palette.warning ? theme.palette.warning.main : "#ed6c02",
    fontWeight: 500,
  },

  counterError: {
    color: theme.palette.error.main,
    fontWeight: 700,
  },

  infoIcon: {
    fontSize: "1.7rem",
    cursor: "help",
    color: "rgba(0,0,0,0.38)",
  },

  progress: {
    marginTop: 4,
    height: 3,
    borderRadius: 2,
  },

  progressNormal: {
    "& .MuiLinearProgress-bar": { backgroundColor: theme.palette.primary.main },
    backgroundColor: "rgba(0,0,0,0.1)",
  },

  progressWarning: {
    "& .MuiLinearProgress-bar": {
      backgroundColor: theme.palette.warning
        ? theme.palette.warning.main
        : "#ed6c02",
    },
    backgroundColor: "rgba(0,0,0,0.1)",
  },

  progressError: {
    "& .MuiLinearProgress-bar": { backgroundColor: theme.palette.error.main },
    backgroundColor: "rgba(0,0,0,0.1)",
  },

  overflowHelper: {
    fontSize: "1.5rem",
    color: theme.palette.error.main,
    marginTop: 2,
  },
}));

const RichTextField = props => {
  const classes = useStyles();
  const {
    value,
    className,
    onBlur,
    onChange,
    enableLinks = false,
    error = false,
    helperText,
    maxLength = 4096,
  } = props;

  // Seed the editor with the sanitized incoming HTML once; the editor owns its
  // content thereafter (output flows back via onChange), so it isn't re-seeded
  // on every parent render.
  const [initialHtml] = useState(() => sanitizeHtml(value || ""));
  const [htmlLength, setHtmlLength] = useState(() => initialHtml.length);

  /** onChange payload: { html, text, length, isOverMax } */
  const handleUpdate = useCallback(
    ({ text, html }) => {
      const sanitized = sanitizeHtml(html);
      if (sanitized === value) {
        return;
      }
      const length = sanitized.length;
      onChange({
        html: sanitized,
        text,
        length,
        isOverMax: length > maxLength,
      });
      setHtmlLength(length);
    },
    [value, onChange, maxLength],
  );

  const ratio = htmlLength / maxLength;
  const isOverMax = htmlLength > maxLength;
  const isWarning = ratio >= 0.9 && !isOverMax;

  const counterClass = isOverMax
    ? classes.counterError
    : isWarning
    ? classes.counterWarning
    : classes.counterNormal;

  const progressClass = isOverMax
    ? classes.progressError
    : isWarning
    ? classes.progressWarning
    : classes.progressNormal;

  return (
    <Fragment>
      <div
        className={className || classes.rte}
        style={{ display: "flex", flexDirection: "column" }}
      >
        <RichTextEditor
          html={initialHtml}
          editable
          enableLinks={enableLinks}
          onUpdate={handleUpdate}
          onBlur={onBlur}
        />
      </div>
      <div className={classes.counterArea}>
        <div
          className={`${classes.counterRow} ${classes.counterText} ${counterClass}`}
        >
          <span>
            {htmlLength.toLocaleString()} / {maxLength.toLocaleString()}
          </span>
          <Tooltip
            title="Text formatting (bold, bullets, headings, links) counts toward the character limit."
            placement="top"
          >
            <span className={classes.infoIcon}>
              <FontAwesomeIcon icon={faCircleInfo} />
            </span>
          </Tooltip>
        </div>
        <LinearProgress
          variant="determinate"
          value={Math.min(100, ratio * 100)}
          className={`${classes.progress} ${progressClass}`}
        />
        {error && (
          <FormHelperText
            error={error}
            style={{ position: "relative", marginTop: 4, top: 0 }}
          >
            {helperText}
          </FormHelperText>
        )}
        {isOverMax && (
          <div className={classes.overflowHelper}>
            Exceeds maximum length by{" "}
            {(htmlLength - maxLength).toLocaleString()} characters.
          </div>
        )}
      </div>
    </Fragment>
  );
};

export default RichTextField;
